<template>
  <div>
    <div class="schedule-presets">
      <button
        v-for="preset in presets"
        :key="preset.label"
        type="button"
        class="schedule-chip schedule-chip--pill"
        :class="{ 'schedule-chip--active': isPreset(preset.days) }"
        :aria-pressed="String(isPreset(preset.days))"
        :data-cy="`schedule-preset-${preset.label.toLowerCase()}`"
        @click="pickPreset(preset.days)"
      >
        {{ preset.label }}
      </button>
      <button
        type="button"
        class="schedule-chip schedule-chip--pill"
        :class="{ 'schedule-chip--active': isCustom }"
        :aria-pressed="String(isCustom)"
        data-cy="schedule-preset-custom"
        @click="startCustom"
      >
        Custom
      </button>
    </div>

    <p v-if="savedEnded" class="schedule-ended" data-cy="schedule-ended">
      {{ savedEndedText }}. Change the schedule and save to restart it.
    </p>

    <div v-if="schedule" class="custom-schedule" data-cy="custom-schedule">
      <div class="custom-schedule__row">
        <span class="custom-schedule__label">Repeat every</span>
        <b-form-input
          v-model.number="schedule.every"
          type="number"
          min="1"
          max="52"
          aria-label="Repeat every"
          class="custom-schedule__every"
          data-cy="schedule-every"
        />
        <b-form-select
          v-model="schedule.repeat"
          :options="repeatOptions"
          aria-label="Repeat unit"
          class="custom-schedule__unit"
          data-cy="schedule-repeat"
        />
      </div>

      <template v-if="schedule.repeat === 'week'">
        <span class="custom-schedule__label">Repeat on</span>
        <div class="schedule-days" role="group" aria-label="Days of the week">
          <button
            v-for="day in dayButtons"
            :key="day.value"
            type="button"
            class="schedule-chip schedule-chip--day"
            :class="{
              'schedule-chip--active': schedule.days_of_week.includes(day.value)
            }"
            :aria-pressed="String(schedule.days_of_week.includes(day.value))"
            :title="day.title"
            @click="toggleCustomDay(day.value)"
          >
            {{ day.letter }}
          </button>
        </div>
      </template>

      <template v-else>
        <b-form-radio-group
          v-model="schedule.month_mode"
          :options="monthModeOptions"
          class="custom-schedule__mode"
          data-cy="schedule-month-mode"
        />
        <div
          v-if="schedule.month_mode === 'date'"
          class="month-dates"
          role="group"
          aria-label="Days of the month"
        >
          <button
            v-for="date in monthDateButtons"
            :key="date.value"
            type="button"
            class="month-date"
            :class="{
              'month-date--active': schedule.month_dates.includes(date.value),
              'month-date--last': date.value === lastDay
            }"
            :aria-pressed="String(schedule.month_dates.includes(date.value))"
            :data-cy="`schedule-date-${date.value}`"
            @click="toggleMonthDate(date.value)"
          >
            {{ date.text }}
          </button>
        </div>
        <div v-else class="custom-schedule__row">
          <span class="custom-schedule__label">On the</span>
          <b-form-select
            v-model="schedule.month_weekday.nth"
            :options="nthOptions"
            aria-label="Which week of the month"
            class="custom-schedule__unit"
          />
          <b-form-select
            v-model="schedule.month_weekday.day"
            :options="weekdayOptions"
            aria-label="Day of the week"
            class="custom-schedule__weekday"
          />
        </div>
      </template>

      <div class="custom-schedule__row">
        <span class="custom-schedule__label">Starts on</span>
        <GroupDateInput
          v-model="schedule.starts_on"
          aria-label="Starts on"
          data-cy="schedule-starts-on"
        />
      </div>

      <span class="custom-schedule__label">Ends</span>
      <div class="custom-schedule__ends" role="radiogroup" aria-label="Ends">
        <b-form-radio
          v-model="schedule.ends.type"
          value="never"
          name="schedule-ends"
          >Never</b-form-radio
        >
        <div class="custom-schedule__end">
          <b-form-radio
            v-model="schedule.ends.type"
            value="on"
            name="schedule-ends"
            >On</b-form-radio
          >
          <GroupDateInput
            v-model="schedule.ends.on"
            aria-label="End date"
            :disabled="schedule.ends.type !== 'on'"
            data-cy="schedule-ends-on"
          />
        </div>
        <div class="custom-schedule__end">
          <b-form-radio
            v-model="schedule.ends.type"
            value="after"
            name="schedule-ends"
            >After</b-form-radio
          >
          <b-form-input
            v-model.number="schedule.ends.after"
            type="number"
            min="1"
            aria-label="Number of sends"
            class="custom-schedule__every"
            :disabled="schedule.ends.type !== 'after'"
            data-cy="schedule-ends-after"
          />
          <span class="custom-schedule__muted">sends</span>
        </div>
      </div>
    </div>

    <div
      v-else
      class="schedule-days"
      role="group"
      aria-label="Days of the week"
    >
      <button
        v-for="day in dayButtons"
        :key="day.value"
        type="button"
        class="schedule-chip schedule-chip--day"
        :class="{ 'schedule-chip--active': days.includes(day.value) }"
        :aria-pressed="String(days.includes(day.value))"
        :title="day.title"
        :data-cy="`schedule-day-${day.value}`"
        @click="toggleDay(day.value)"
      >
        {{ day.letter }}
      </button>
    </div>

    <slot name="send-time" />

    <div
      v-if="schedule"
      class="schedule-summary"
      aria-live="polite"
      data-cy="schedule-summary"
    >
      <template v-if="scheduleProblemText">{{ scheduleProblemText }}</template>
      <template v-else>
        <strong>{{ scheduleSentence }}</strong>
        <span v-if="upcomingSends.length">
          Next: {{ upcomingSends.join(' · ') }}
        </span>
        <span v-else>No upcoming sends.</span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import {
  DAY_NAMES,
  LAST_DAY,
  NTH_OPTIONS,
  Schedule,
  defaultSchedule,
  describeSchedule,
  endedText,
  hasEnded,
  isScheduleChanged,
  nextSendDates,
  normalizeSchedule,
  scheduleProblem,
  timeLabel
} from './schedule'
import { Component, Prop, Vue } from 'vue-property-decorator'
import GroupDateInput from './GroupDateInput.vue'

/** What the editor reads of the saved automation: its schedule, send count and active flag. */
interface SavedAutomation {
  is_active: boolean
  schedule?: Schedule | null
  sends_count?: number
}

// Stored 0 = Monday … 6 = Sunday (the BlytzCollect convention); shown Sun..Sat.
const DAY_BUTTONS = [6, 0, 1, 2, 3, 4, 5].map(value => ({
  value,
  letter: DAY_NAMES[value][0],
  title: DAY_NAMES[value]
}))
const PRESETS = [
  { label: 'Daily', days: [0, 1, 2, 3, 4, 5, 6] },
  { label: 'Weekdays', days: [0, 1, 2, 3, 4] }
]
const MONTH_DATE_BUTTONS = [
  ...Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    text: String(i + 1)
  })),
  { value: LAST_DAY, text: 'Last day' }
]

/**
 * The automation schedule: Daily / Weekdays / day circles, or a Custom recurrence with a summary and
 * the next three sends. `days`, `schedule` (null when not custom) and `sendTime` sync with GroupForm;
 * the Send Time field itself comes in through the `send-time` slot.
 */
@Component({ components: { GroupDateInput } })
export default class GroupScheduleEditor extends Vue {
  @Prop({ type: Array, required: true }) days!: number[]
  @Prop({ type: Object, default: null }) schedule!: Schedule | null
  @Prop({ type: String, required: true }) sendTime!: string
  @Prop({ type: Object, default: null }) saved!: SavedAutomation | null

  // Constant option lists, as plain data: the template reads them and nothing changes them.
  dayButtons = DAY_BUTTONS
  presets = PRESETS
  monthModeOptions = [
    { value: 'date', text: 'On day(s)' },
    { value: 'weekday', text: 'On the…' }
  ]
  monthDateButtons = MONTH_DATE_BUTTONS
  lastDay = LAST_DAY
  nthOptions = NTH_OPTIONS
  weekdayOptions = DAY_NAMES.map((text, value) => ({ value, text }))

  get isCustom(): boolean {
    return this.schedule !== null
  }

  get repeatOptions() {
    const plural = (this.schedule?.every ?? 1) === 1 ? '' : 's'

    return [
      { value: 'week', text: `week${plural}` },
      { value: 'month', text: `month${plural}` }
    ]
  }

  /** Whether the saved automation's custom schedule ran out (messaging-service paused it). */
  get savedEnded(): boolean {
    return hasEnded(this.saved)
  }

  get savedEndedText(): string {
    const saved = normalizeSchedule(this.saved?.schedule)

    return saved ? endedText(saved) : ''
  }

  get scheduleProblemText(): string | null {
    return this.schedule ? scheduleProblem(this.schedule) : null
  }

  get scheduleSentence(): string {
    return this.schedule
      ? describeSchedule(this.schedule, timeLabel(this.sendTime))
      : ''
  }

  /** The next three send days, so merchants can check the pattern before saving. */
  get upcomingSends(): string[] {
    if (!this.schedule) return []
    // The saved count only applies while the saved schedule is unchanged; core resets it on change.
    const sendsCount = isScheduleChanged(this.saved?.schedule, this.schedule)
      ? 0
      : (this.saved?.sends_count ?? 0)

    return nextSendDates(this.schedule, moment(), 3, sendsCount).map(date =>
      date.format('ddd, MMM D')
    )
  }

  startCustom() {
    if (!this.schedule)
      this.$emit('update:schedule', defaultSchedule(this.days))
  }

  async pickPreset(days: number[]) {
    if (this.schedule) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Switch back to a simple weekly schedule? The custom schedule is discarded.',
        { title: 'Custom schedule', okTitle: 'Switch', okVariant: 'blue' }
      )
      if (!ok) return
      this.$emit('update:schedule', null)
    }
    this.$emit('update:days', [...days])
  }

  isPreset(days: number[]): boolean {
    if (this.schedule) return false
    const chosen = [...this.days].sort()

    return (
      chosen.length === days.length && chosen.every((d, i) => d === days[i])
    )
  }

  toggleDay(day: number) {
    this.$emit(
      'update:days',
      this.days.includes(day)
        ? this.days.filter(d => d !== day)
        : [...this.days, day].sort()
    )
  }

  toggleCustomDay(day: number) {
    const schedule = this.schedule
    if (!schedule) return
    schedule.days_of_week = schedule.days_of_week.includes(day)
      ? schedule.days_of_week.filter(d => d !== day)
      : [...schedule.days_of_week, day].sort()
  }

  toggleMonthDate(date: number) {
    const schedule = this.schedule
    if (!schedule) return
    schedule.month_dates = schedule.month_dates.includes(date)
      ? schedule.month_dates.filter(d => d !== date)
      : [...schedule.month_dates, date]
  }
}
</script>

<style lang="scss" scoped>
$ink: #1f2937;
$line: #c7ced8;

.schedule-presets,
.schedule-days {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.schedule-days {
  margin-bottom: 22px;
}

// Daily/Weekdays pills and the day circles share one look.
.schedule-chip {
  color: $ink;

  font-size: 14px;
  font-weight: 600;

  background: #fff;
  border: 1px solid $line;
  cursor: pointer;

  &--pill {
    padding: 8px 16px;

    border-radius: 999px;
  }

  &--day {
    width: 36px;
    height: 36px;

    border-radius: 50%;
  }

  &--active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }
}

// The Custom recurrence editor, laid out like a calendar app's "Custom recurrence" panel.
.custom-schedule {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 22px;

  .schedule-days {
    margin-bottom: 0;
  }
}

.custom-schedule__row,
.custom-schedule__end {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.custom-schedule__label {
  min-width: 96px;
  color: $ink;

  font-size: 14px;
  font-weight: 600;
}

.custom-schedule__muted {
  color: $darker-gray;

  font-size: 14px;
}

.custom-schedule__every {
  width: 76px;
}

.custom-schedule__unit {
  width: 120px;
}

.custom-schedule__weekday {
  width: 150px;
}

.custom-schedule__ends {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .custom-control {
    min-width: 72px;
  }
}

// 1–31 plus "Last day", seven to a row like a month view.
.month-dates {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  gap: 6px;
}

.month-date {
  height: 32px;
  color: $ink;

  font-size: 13px;
  font-weight: 600;

  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;
  cursor: pointer;

  &--last {
    grid-column: span 3;
  }

  &--active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }
}

.schedule-ended {
  margin-bottom: 14px;
  padding: 10px 14px;
  color: $ink;

  font-size: 14px;

  background: #fff8e6;
  border: 1px solid #f5d38a;
  border-radius: 8px;
}

.schedule-summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: $darker-gray;

  font-size: 14px;

  strong {
    color: $ink;
  }
}
</style>
